import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Step1NameBio } from './Step1NameBio';
import { Step2Personality } from './Step2Personality';
import { Step3Relationship } from './Step3Relationship';
import { Step4Job } from './Step4Job';
import { Step5Hobby } from './Step5Hobby';
import { Step6Appearance } from './Step6Appearance';
import { WizardProgress } from './WizardProgress';
interface UnleashWizardProps {
  onComplete: (angelData: AngelData) => void;
  onBack: () => void;
}
export interface AngelData {
  name: string;
  bio: string;
  personality: string;
  relationship: string;
  job: string;
  hobby: string;
  appearance: {
    hairstyle: string;
    skinTone: string;
    eyeColor: string;
    outfitStyle: string;
  };
}
export const UnleashWizard: React.FC<UnleashWizardProps> = ({
  onComplete,
  onBack
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [angelData, setAngelData] = useState<AngelData>(() => {
    // Try to load saved progress from localStorage
    const savedData = localStorage.getItem('unleashWizardProgress');
    if (savedData) {
      return JSON.parse(savedData);
    }
    return {
      name: '',
      bio: '',
      personality: '',
      relationship: '',
      job: '',
      hobby: '',
      appearance: {
        hairstyle: '',
        skinTone: '',
        eyeColor: '',
        outfitStyle: ''
      }
    };
  });
  // Save progress to localStorage whenever angelData changes
  useEffect(() => {
    localStorage.setItem('unleashWizardProgress', JSON.stringify(angelData));
  }, [angelData]);
  const totalSteps = 6;
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Wizard completed
      onComplete(angelData);
    }
  };
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      // User wants to go back from first step
      onBack();
    }
  };
  const updateAngelData = (data: Partial<AngelData>) => {
    setAngelData(prev => ({
      ...prev,
      ...data
    }));
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1NameBio name={angelData.name} bio={angelData.bio} onUpdate={(name, bio) => updateAngelData({
          name,
          bio
        })} onNext={goToNextStep} data-id="element-22339" />;
      case 2:
        return <Step2Personality selectedPersonality={angelData.personality} onSelect={personality => updateAngelData({
          personality
        })} onNext={goToNextStep} onBack={goToPreviousStep} data-id="element-22340" />;
      case 3:
        return <Step3Relationship selectedRelationship={angelData.relationship} onSelect={relationship => updateAngelData({
          relationship
        })} onNext={goToNextStep} onBack={goToPreviousStep} data-id="element-22341" />;
      case 4:
        return <Step4Job selectedJob={angelData.job} onSelect={job => updateAngelData({
          job
        })} onNext={goToNextStep} onBack={goToPreviousStep} data-id="element-22342" />;
      case 5:
        return <Step5Hobby selectedHobby={angelData.hobby} onSelect={hobby => updateAngelData({
          hobby
        })} onNext={goToNextStep} onBack={goToPreviousStep} data-id="element-22343" />;
      case 6:
        return <Step6Appearance appearance={angelData.appearance} onUpdate={appearance => updateAngelData({
          appearance
        })} onComplete={() => onComplete(angelData)} onBack={goToPreviousStep} data-id="element-22344" />;
      default:
        return null;
    }
  };
  return <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden" data-id="element-22345">
      <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-600 text-white" data-id="element-22346">
        <h1 className="text-2xl font-bold" data-id="element-22347">Unleash Your Personal Angel</h1>
        <p className="text-pink-100" data-id="element-22348">
          Create your perfect companion in a few simple steps
        </p>
      </div>
      <WizardProgress currentStep={currentStep} totalSteps={totalSteps} data-id="element-22349" />
      <div className="p-6" data-id="element-22350">{renderStep()}</div>
    </div>;
};